import React, { useEffect, useState, useCallback, useRef } from "react";
import { useNavigate, useParams, useLocation } from "react-router-dom";
import toast from "react-hot-toast";
import { FiSave, FiUserPlus, FiKey, FiChevronDown, FiChevronUp } from "react-icons/fi";
import Sidebar from "../components/Sidebar";
import Navbar from "../components/Navbar";
import PasswordInput from "../components/PasswordInput";

const baseUrl = process.env.REACT_APP_API_BASE_URL;
const ACTIONS = ["view", "create", "edit", "delete"];

// A few backend modules don't (yet) have a matching sidebar page — granting
// them changes what the API allows, but there's nothing new to click on.
// Flagged here so it's obvious instead of looking like a bug.
const MODULE_NOTES = {
  analytics: "Dashboard widget only — no separate page",
  "blocked-ips": "No frontend page yet",
};

const authHeaders = (token, json = true) => ({
  Authorization: `Bearer ${token}`,
  ...(json ? { "Content-Type": "application/json" } : {}),
});

const StaffForm = () => {
  const { id } = useParams();
  const isEdit = !!id;
  const navigate = useNavigate();
  const location = useLocation();
  const token = localStorage.getItem("token");

  const [form, setForm] = useState({ name: "", email: "", password: "", roleId: "" });
  const [roles, setRoles] = useState([]);
  const [catalog, setCatalog] = useState([]); // [{module,action}]
  const [effective, setEffective] = useState({}); // "module:action" -> bool
  const [roleDefaults, setRoleDefaults] = useState(new Set());
  const [loading, setLoading] = useState(isEdit);
  const [submitting, setSubmitting] = useState(false);
  const [showMatrix, setShowMatrix] = useState(false);
  const [newPassword, setNewPassword] = useState("");
  const [resetting, setResetting] = useState(false);
  const [savingPermissions, setSavingPermissions] = useState(false);
  const passwordRef = useRef(null);
  const matrixRef = useRef(null);

  const loadRoleDefaults = useCallback(async (roleId) => {
    if (!roleId) { setRoleDefaults(new Set()); return new Set(); }
    const res = await fetch(`${baseUrl}/role/view/${roleId}`, { headers: authHeaders(token, false) });
    const result = await res.json();
    const set = new Set((result?.data?.permissions || []).map((p) => `${p.module}:${p.action}`));
    setRoleDefaults(set);
    return set;
  }, [token]);

  useEffect(() => {
    (async () => {
      try {
        const [rolesRes, catalogRes] = await Promise.all([
          fetch(`${baseUrl}/role/list`, { headers: authHeaders(token, false) }),
          fetch(`${baseUrl}/role/permissions`, { headers: authHeaders(token, false) }),
        ]);
        const rolesResult = await rolesRes.json();
        const catalogResult = await catalogRes.json();
        if (rolesResult.status) setRoles(rolesResult.data || []);
        if (catalogResult.status) setCatalog(catalogResult.data || []);

        if (isEdit) {
          const staffRes = await fetch(`${baseUrl}/staff/view/${id}`, { headers: authHeaders(token, false) });
          const staffResult = await staffRes.json();
          if (!staffResult.status) throw new Error(staffResult.message || "Staff not found");
          setForm({ name: staffResult.data.name || "", email: staffResult.data.email || "", password: "", roleId: staffResult.data.roleId || "" });

          const defaults = await loadRoleDefaults(staffResult.data.roleId);

          const permRes = await fetch(`${baseUrl}/staff/${id}/permissions`, { headers: authHeaders(token, false) });
          const permResult = await permRes.json();
          if (permResult.status) {
            const map = {};
            (permResult.data || []).forEach((p) => { map[`${p.module}:${p.action}`] = p.effective; });
            setEffective(map);
          } else {
            setEffective(Object.fromEntries(Array.from(defaults).map((k) => [k, true])));
          }

          if (location.state?.focus === "password") {
            setTimeout(() => passwordRef.current?.scrollIntoView({ behavior: "smooth", block: "center" }), 200);
          }
          if (location.state?.focus === "permissions") {
            setShowMatrix(true);
            setTimeout(() => matrixRef.current?.scrollIntoView({ behavior: "smooth", block: "start" }), 200);
          }
        }
      } catch (err) {
        toast.error(err.message);
      } finally {
        setLoading(false);
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const handleRoleChange = async (roleId) => {
    setForm((f) => ({ ...f, roleId }));
    if (isEdit) {
      const defaults = await loadRoleDefaults(roleId);
      setEffective(Object.fromEntries(Array.from(defaults).map((k) => [k, true])));
      toast("Switched to the new role's default permissions — adjust the matrix below if this staff needs exceptions.", { icon: "ℹ️" });
    }
  };

  const toggle = (module, action) => {
    const key = `${module}:${action}`;
    setEffective((prev) => ({ ...prev, [key]: !prev[key] }));
  };

  const grouped = catalog.reduce((acc, p) => {
    (acc[p.module] = acc[p.module] || []).push(p.action);
    return acc;
  }, {});

  /**
   * Pushes the current checkbox state to the server as the staff member's
   * complete override set. Shared by the top "Save Changes" button (saves
   * profile + permissions together) and the matrix's own "Save Permissions"
   * button (saves just this, without leaving the page).
   */
  const savePermissions = async () => {
    const overrides = Object.keys(effective)
      .filter((key) => effective[key] !== roleDefaults.has(key))
      .map((key) => {
        const [module, action] = key.split(":");
        return { module, action, allow: !!effective[key] };
      });
    const permRes = await fetch(`${baseUrl}/staff/${id}/permissions`, {
      method: "PUT",
      headers: authHeaders(token),
      body: JSON.stringify({ overrides }),
    });
    const permResult = await permRes.json();
    if (!permResult.status) throw new Error(permResult.message || "Permission update failed");
  };

  const handleSaveOverridesOnly = async () => {
    setSavingPermissions(true);
    try {
      await savePermissions();
      toast.success("Permissions updated successfully");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSavingPermissions(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.roleId) { toast.error("Please select a role."); return; }
    setSubmitting(true);
    try {
      if (isEdit) {
        const res = await fetch(`${baseUrl}/staff/update/${id}`, {
          method: "PUT",
          headers: authHeaders(token),
          body: JSON.stringify({ name: form.name, email: form.email, roleId: Number(form.roleId) }),
        });
        const result = await res.json();
        if (!result.status) throw new Error(result.message || "Update failed");

        await savePermissions();

        toast.success("Staff updated successfully");
      } else {
        const res = await fetch(`${baseUrl}/staff/create`, {
          method: "POST",
          headers: authHeaders(token),
          body: JSON.stringify({ ...form, roleId: Number(form.roleId) }),
        });
        const result = await res.json();
        if (!result.status) throw new Error(result.message || "Create failed");
        toast.success("Staff created successfully");
      }
      navigate("/staff");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const handleResetPassword = async () => {
    if (newPassword.length < 8) { toast.error("Password must be at least 8 characters."); return; }
    setResetting(true);
    try {
      const res = await fetch(`${baseUrl}/staff/reset-password/${id}`, {
        method: "PUT",
        headers: authHeaders(token),
        body: JSON.stringify({ newPassword }),
      });
      const result = await res.json();
      if (!result.status) throw new Error(result.message || "Reset failed");
      toast.success("Password reset — staff has been logged out everywhere.");
      setNewPassword("");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setResetting(false);
    }
  };

  if (loading) {
    return (
      <div className="flex bg-gray-100 min-h-screen">
        <Sidebar /><div className="flex-1 flex flex-col min-w-0"><Navbar />
          <div className="flex justify-center items-center py-24">
            <div className="w-7 h-7 border-4 border-blue-400 border-t-transparent rounded-full animate-spin" />
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex bg-gray-100 min-h-screen">
      <Sidebar />
      <div className="flex-1 flex flex-col min-w-0">
        <Navbar />
        <div className="p-5 sm:p-8 space-y-6">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-100 flex items-center justify-center">
              <FiUserPlus size={20} className="text-indigo-600" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-gray-800">{isEdit ? "Edit Staff" : "Add Staff"}</h2>
              <p className="text-sm text-gray-400 mt-0.5">{isEdit ? "Update details, role and permission overrides" : "Create a new staff login"}</p>
            </div>
          </div>

          <form onSubmit={handleSubmit} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 max-w-2xl space-y-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5">Full Name</label>
                <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 mb-1.5">Email</label>
                <input required type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-300" />
              </div>
            </div>

            {!isEdit && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">Password</label>
                  <PasswordInput required minLength={8} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-500 mb-1.5">Role</label>
                  <select required value={form.roleId} onChange={(e) => handleRoleChange(e.target.value)}
                    className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-300">
                    <option value="">Select a role...</option>
                    {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                  </select>
                </div>
              </div>
            )}

            {isEdit && (
              <div className="sm:w-1/2 sm:pr-2">
                <label className="block text-xs font-medium text-gray-500 mb-1.5">Role</label>
                <select required value={form.roleId} onChange={(e) => handleRoleChange(e.target.value)}
                  className="w-full px-3.5 py-2.5 border border-gray-200 rounded-xl text-sm text-gray-700 focus:outline-none focus:ring-2 focus:ring-blue-300">
                  <option value="">Select a role...</option>
                  {roles.map((r) => <option key={r.id} value={r.id}>{r.name}</option>)}
                </select>
              </div>
            )}

            <div className="flex gap-3 pt-2">
              <button type="submit" disabled={submitting}
                className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-sm font-medium px-5 py-2.5 rounded-lg transition">
                <FiSave size={14} /> {submitting ? "Saving..." : isEdit ? "Save Changes" : "Create Staff"}
              </button>
              <button type="button" onClick={() => navigate("/staff")} className="px-5 py-2.5 text-sm font-medium text-gray-600 bg-gray-100 hover:bg-gray-200 rounded-lg transition">
                Cancel
              </button>
            </div>
          </form>

          {isEdit && (
            <>
              <div ref={matrixRef} className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
                <button type="button" onClick={() => setShowMatrix((s) => !s)}
                  className="w-full flex items-center justify-between px-6 py-4 text-left">
                  <div>
                    <h3 className="text-sm font-semibold text-gray-700">Individual Permission Overrides</h3>
                    <p className="text-xs text-gray-400 mt-0.5">Grants checked here beyond the role, or revokes ones from it, for this staff member only.</p>
                  </div>
                  {showMatrix ? <FiChevronUp /> : <FiChevronDown />}
                </button>
                {showMatrix && (
                  <div className="px-6 pb-6">
                    <div className="border border-gray-100 rounded-xl overflow-hidden overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="text-left text-xs text-gray-400 uppercase tracking-wide bg-gray-50">
                            <th className="py-3 pl-5 pr-4 font-semibold">Module</th>
                            {ACTIONS.map((a) => <th key={a} className="py-3 px-4 text-center font-semibold capitalize">{a}</th>)}
                          </tr>
                        </thead>
                        <tbody className="divide-y divide-gray-100">
                          {Object.keys(grouped).map((module) => (
                            <tr key={module} className="odd:bg-white even:bg-gray-50/40 hover:bg-blue-50/40 transition">
                              <td className="py-3 pl-5 pr-4 font-medium text-gray-700 capitalize">
                                {module.replace(/-/g, " ")}
                                {MODULE_NOTES[module] && (
                                  <span className="block text-[11px] text-amber-600/80 normal-case mt-0.5">{MODULE_NOTES[module]}</span>
                                )}
                              </td>
                              {ACTIONS.map((action) => {
                                const key = `${module}:${action}`;
                                const has = grouped[module].includes(action);
                                const checked = !!effective[key];
                                const isOverride = has && checked !== roleDefaults.has(key);
                                return (
                                  <td key={action} className="py-3 px-4 text-center">
                                    {has ? (
                                      <input
                                        type="checkbox"
                                        checked={checked}
                                        onChange={() => toggle(module, action)}
                                        className={`rounded cursor-pointer ${isOverride ? "accent-amber-500" : "accent-blue-600"}`}
                                        style={{ width: 18, height: 18 }}
                                        title={isOverride ? "Overridden from role default" : ""}
                                      />
                                    ) : (
                                      <span className="text-gray-200">—</span>
                                    )}
                                  </td>
                                );
                              })}
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="flex items-center justify-between mt-4 flex-wrap gap-2">
                      <p className="text-xs text-gray-400">Amber checkboxes are overrides that differ from the selected role's defaults.</p>
                      <button type="button" onClick={handleSaveOverridesOnly} disabled={savingPermissions}
                        className="flex items-center gap-2 bg-blue-600 hover:bg-blue-700 disabled:opacity-60 text-white text-sm font-medium px-5 py-2.5 rounded-lg transition">
                        <FiSave size={14} /> {savingPermissions ? "Saving..." : "Save Permissions"}
                      </button>
                    </div>
                  </div>
                )}
              </div>

              <div ref={passwordRef} className="bg-white rounded-xl shadow-sm border border-gray-100 p-6 max-w-2xl space-y-3">
                <h3 className="text-sm font-semibold text-gray-700 flex items-center gap-2"><FiKey size={14} className="text-purple-500" /> Reset Password</h3>
                <p className="text-xs text-gray-400">Sets a new password for this staff member and logs them out of every active session.</p>
                <div className="flex gap-3 items-start flex-wrap">
                  <PasswordInput minLength={8} placeholder="New password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)}
                    className="flex-1 min-w-[200px]" />
                  <button type="button" onClick={handleResetPassword} disabled={resetting}
                    className="px-5 py-2.5 bg-purple-600 hover:bg-purple-700 disabled:opacity-60 text-white text-sm font-medium rounded-lg transition">
                    {resetting ? "Resetting..." : "Reset Password"}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default StaffForm;
